// Seed data for Invitation - External (DMUT).
//
// TODO(API): placeholder until the external-invitation contract lands.
//
// Field names deliberately match what planningApi.js's normalizers already
// produce (cargoType / ot / volume / ddrFrom / ddrTo / ddrNotes / incoterms /
// incotermsNotes / plant / unit / crudes[].{crudeName,maxCapacity,
// remarkMaxCapacity,isViaCibOnly,bleeding1,bleeding2}) so the shared read-only
// CargoTable renders these rows unchanged, and so a live endpoint returning the
// Planning shape drops straight in.
//
// Statuses cover all four cases the DOD calls out, including a derived Expired
// (an Open invitation whose Submission Deadline has already passed) — see
// invitationExternalStore.deriveStatus.

import { INVITATION_STATUS, DOC_SOURCE } from './invitationExternalConstants';

// A cargo + crude set reused across records — the DMUT-facing view is read-only,
// so records never diverge by mutation and can share this shape.
const cargo = (over = {}) => ({
  id: 'cargo-1',
  cargoType: 'Light',
  ot: 10,
  volume: 100,
  ddrFrom: '2026-09-01T00:00:00+00:00',
  ddrTo: '2026-09-10T00:00:00+00:00',
  ddrNotes: 'Laycan subject to terminal confirmation',
  incoterms: ['FOB'],
  incotermsNotes: 'FOB Dumai, load port cost on seller',
  plant: 'RU II Dumai',
  unit: 'CDU DMI',
  crudes: [
    {
      id: 'crude-1',
      crudeName: 'BREGA',
      maxCapacity: 50,
      remarkMaxCapacity: 'Limited by desalter capacity',
      isViaCibOnly: false,
      bleeding1: 10,
      bleeding2: 5,
    },
    {
      id: 'crude-2',
      crudeName: 'SENIPAH CONDENSATE',
      maxCapacity: 30,
      remarkMaxCapacity: '',
      isViaCibOnly: true,
      bleeding1: 8,
      bleeding2: null,
    },
  ],
  ...over,
});

const clauses = () => [
  {
    clauseId: 'clause-1',
    title: 'Price DAP',
    mandatory: true,
    summernote:
      'Price shall be based on the average of Platts quotations for the three days after the Bill of Lading date.',
  },
  {
    clauseId: 'clause-2',
    title: 'Quality & Quantity Determination',
    mandatory: false,
    summernote:
      'Quality and quantity as determined by an independent inspector at the load port shall be final and binding.',
  },
];

const documents = () => [
  {
    id: 'doc-1',
    name: 'General Terms & Conditions',
    source: DOC_SOURCE.PLANNING,
    showToDmut: true,
    fileName: 'gtc-crude-2026.pdf',
    fileContentType: 'application/pdf',
    lastUpdated: '2026-07-18T11:30:00+00:00',
  },
  {
    id: 'doc-2',
    name: 'Assay Summary Sheet',
    source: DOC_SOURCE.PLANNING,
    showToDmut: true,
    fileName: 'assay-summary.xlsx',
    fileContentType:
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    lastUpdated: '2026-07-19T08:05:00+00:00',
  },
  {
    id: 'doc-3',
    name: 'Tender Instruction to Bidders',
    source: DOC_SOURCE.PROCUREMENT,
    showToDmut: true,
    fileName: 'instruction-to-bidders.pdf',
    fileContentType: 'application/pdf',
    lastUpdated: '2026-07-21T14:00:00+00:00',
  },
  {
    // Excluded from the tab: only showToDmut = true is visible to the DMUT.
    // Kept in the seed so the filter is exercised rather than assumed.
    id: 'doc-4',
    name: 'Internal HPS Working Paper',
    source: DOC_SOURCE.PROCUREMENT,
    showToDmut: false,
    fileName: 'hps-internal.xlsx',
    fileContentType:
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    lastUpdated: '2026-07-22T09:15:00+00:00',
  },
];

const record = ({
  id,
  procId,
  title,
  description,
  status,
  submissionDeadline,
  validityPeriod,
  declineReason = null,
  respondedAt = null,
  contractType = 'Term',
}) => ({
  id,
  procId,
  title,
  procurementType: 'Crude dan Condensat',
  procurementMethod: 'Pelelangan khusus',
  description,
  invitationStatus: status,
  declineReason,
  respondedAt,
  // Set when the DMUT accepts — the DOD says the Offer status becomes "Open"
  // after Accept. No Offer screen consumes this yet; it is recorded here so the
  // transition is modelled rather than lost. See invitationExternalStore.accept.
  offerStatus: status === INVITATION_STATUS.ACCEPTED ? 'Open' : null,
  requirement: {
    contractType,
    startPeriod: '2026-09-01T00:00:00+00:00',
    endPeriod: '2027-08-31T00:00:00+00:00',
    cargos: [cargo()],
    clauses: clauses(),
    additionalNotes:
      'Seller reserves the right to reject any and all offers without stating a reason.',
    validity: { submissionDeadline, validityPeriod },
  },
  supportingDocuments: documents(),
});

export const SEED_INVITATIONS = [
  record({
    id: 'inv-1',
    procId: 'CRUDE-20072026-0001',
    title: 'Crude Term Q3 2026 - RU II Dumai',
    description: 'Term procurement of light sweet crude for RU II Dumai CDU.',
    status: INVITATION_STATUS.OPEN,
    // Far-future deadline — stays Open so Accept / Decline are reachable.
    submissionDeadline: '2027-12-15T17:00:00+00:00',
    validityPeriod: '2027-12-31T17:00:00+00:00',
  }),
  record({
    id: 'inv-2',
    procId: 'CRUDE-21072026-0002',
    title: 'Condensate Spot Cargo - RU III Plaju',
    description: 'Single spot condensate cargo, September laycan.',
    status: INVITATION_STATUS.OPEN,
    submissionDeadline: '2027-11-20T17:00:00+00:00',
    validityPeriod: '2027-12-05T17:00:00+00:00',
    contractType: 'Spot',
  }),
  record({
    id: 'inv-3',
    procId: 'CRUDE-15072026-0003',
    title: 'Crude Term H2 2026 - RU IV Cilacap',
    description: 'Medium sour crude term contract for RU IV Cilacap.',
    status: INVITATION_STATUS.ACCEPTED,
    submissionDeadline: '2026-07-25T17:00:00+00:00',
    validityPeriod: '2026-08-10T17:00:00+00:00',
    respondedAt: '2026-07-22T10:12:00+00:00',
  }),
  record({
    id: 'inv-4',
    procId: 'CRUDE-14072026-0004',
    title: 'Crude Spot Cargo - RU V Balikpapan',
    description: 'Heavy crude spot cargo for RU V Balikpapan.',
    status: INVITATION_STATUS.DECLINED,
    submissionDeadline: '2026-07-24T17:00:00+00:00',
    validityPeriod: '2026-08-08T17:00:00+00:00',
    respondedAt: '2026-07-20T15:40:00+00:00',
    declineReason: 'No vessel availability for the requested laycan window.',
    contractType: 'Spot',
  }),
  record({
    id: 'inv-5',
    procId: 'CRUDE-01062026-0005',
    title: 'Crude Term Q2 2026 - RU VI Balongan',
    description: 'Light sweet crude term contract for RU VI Balongan.',
    // Stored as Open with a deadline already past — the store derives Expired,
    // which is exactly the case the DOD describes.
    status: INVITATION_STATUS.OPEN,
    submissionDeadline: '2026-06-10T17:00:00+00:00',
    validityPeriod: '2026-06-25T17:00:00+00:00',
  }),
  record({
    id: 'inv-6',
    procId: 'CRUDE-22072026-0006',
    title: 'Condensate Term 2027 - RU III Plaju',
    description: 'Annual condensate term supply for RU III Plaju.',
    status: INVITATION_STATUS.ACCEPTED,
    submissionDeadline: '2026-07-26T17:00:00+00:00',
    validityPeriod: '2026-08-12T17:00:00+00:00',
    respondedAt: '2026-07-23T09:00:00+00:00',
  }),
  record({
    id: 'inv-7',
    procId: 'CRUDE-19072026-0007',
    title: 'Crude Spot Cargo - RU II Dumai',
    description: 'Opportunistic light crude spot purchase.',
    status: INVITATION_STATUS.DECLINED,
    submissionDeadline: '2026-07-23T17:00:00+00:00',
    validityPeriod: '2026-08-06T17:00:00+00:00',
    respondedAt: '2026-07-21T11:20:00+00:00',
    declineReason: 'Offered grade does not meet our export commitments.',
    contractType: 'Spot',
  }),
  record({
    id: 'inv-8',
    procId: 'CRUDE-10052026-0008',
    title: 'Crude Term Q1 2026 - RU IV Cilacap',
    description: 'Expired term invitation, retained for history.',
    status: INVITATION_STATUS.OPEN,
    submissionDeadline: '2026-05-20T17:00:00+00:00',
    validityPeriod: '2026-06-04T17:00:00+00:00',
  }),
  record({
    id: 'inv-9',
    procId: 'CRUDE-23072026-0009',
    title: 'Crude Term Q4 2026 - RU V Balikpapan',
    description: 'Medium crude term contract covering Q4 2026.',
    status: INVITATION_STATUS.OPEN,
    submissionDeadline: '2027-10-30T17:00:00+00:00',
    validityPeriod: '2027-11-15T17:00:00+00:00',
  }),
  record({
    id: 'inv-10',
    procId: 'CRUDE-23072026-0010',
    title: 'Condensate Spot Cargo - RU VI Balongan',
    description: 'Prompt condensate cargo, October delivery.',
    status: INVITATION_STATUS.OPEN,
    submissionDeadline: '2027-10-05T17:00:00+00:00',
    validityPeriod: '2027-10-20T17:00:00+00:00',
    contractType: 'Spot',
  }),
  record({
    id: 'inv-11',
    procId: 'CRUDE-18072026-0011',
    title: 'Crude Term 2027 - RU II Dumai',
    description: 'Twelfth record — exists so pagination has a second page.',
    status: INVITATION_STATUS.ACCEPTED,
    submissionDeadline: '2026-07-27T17:00:00+00:00',
    validityPeriod: '2026-08-14T17:00:00+00:00',
    respondedAt: '2026-07-23T13:45:00+00:00',
  }),
  record({
    id: 'inv-12',
    procId: 'CRUDE-17072026-0012',
    title: 'Crude Spot Cargo - RU III Plaju',
    description: 'Spot cargo invitation declined on commercial grounds.',
    status: INVITATION_STATUS.DECLINED,
    submissionDeadline: '2026-07-22T17:00:00+00:00',
    validityPeriod: '2026-08-05T17:00:00+00:00',
    respondedAt: '2026-07-19T16:30:00+00:00',
    declineReason: 'Price basis not acceptable.',
    contractType: 'Spot',
  }),
];
